import {useEffect, useMemo, useState} from "react";

import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import useMenuDates from "@globals/hooks/useMenuDates";
import {DateString} from "@globals/types/utilityTypes";
import {useUser} from "@globals/user";
import {hasPDL} from "@globals/user/UserService";
import {getDayPartLocation} from "@globals/user/dayPartUtils";

import useMenu from "@components/Menu/hooks/useMenu";
import {MenuType} from "@components/Menu/interfaces";

import {DailyMenuFilters, DailyMenuSearchParam} from "../interfaces";
import useSelectedMenuDate from "./useSelectedMenuDate";

export default function useDailyMenu(): DailyMenuFilters {
  const {user, isLunchVisible} = useUser();
  const [sortOption, setSortOption] = useState<number>(0);
  const [warning, setWarning] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams();
  const nextSearchParams = new URLSearchParams(searchParams);
  const navigate = useNavigate();

  const hasNoLunchAccess = !isLunchVisible();
  const preSelectedItem = {
    slug: searchParams.get(DailyMenuSearchParam.Item) || undefined,
    closeCallback: () => {
      nextSearchParams.delete(DailyMenuSearchParam.Item);
      setSearchParams(nextSearchParams);
    },
  };
  const sortOrder = searchParams.get(DailyMenuSearchParam.SortOrder);
  const dayPart = searchParams.get(DailyMenuSearchParam.DayPart);

  const clearDayPartSearchParam = () => {
    nextSearchParams.delete(DailyMenuSearchParam.DayPart);
    setSearchParams(nextSearchParams);
  };

  const selectedLocationId = useMemo<number | undefined>(() => {
    if (dayPart && user) {
      const location = getDayPartLocation(user, dayPart);
      if (location) {
        return location.id;
      }
      // Day Part param is invalid
      setWarning(
        `We couldn't find a matching ${dayPart} location – the link you followed may be broken. Please confirm your location is correct!`
      );
    }
    return user?.selected_location.id;
  }, [user, dayPart]);

  useEffect(() => {
    // If the day_part query param is invalid, clear it (this needs to be done in useEffect())
    if (warning) {
      clearDayPartSearchParam();
    }
  }, [warning]);

  const {
    menuDates,
    closedDates,
    holidayDetails,
    isError: isMenuDatesError,
  } = useMenuDates({
    locationId: selectedLocationId,
    disabled: !selectedLocationId,
  });

  const {isDateAdjusted, latestError, selectedDate} = useSelectedMenuDate({
    menuDates,
  });

  const isDayClosed = useMemo<boolean>(() => {
    return closedDates.some((date: DateString) => date === selectedDate);
  }, [closedDates, selectedDate]);

  const menuParams = {
    selectedDate: selectedDate as DateString,
    menuType: MenuType.Individual,
    locationId: selectedLocationId,
    enabled: Boolean(selectedDate && !latestError && !hasNoLunchAccess),
  };

  const {menu} = useMenu(menuParams);

  const handleUpdateSort = (index: number) => {
    setSortOption(index);
    const optionName = menu?.sort_options[index].name as string;
    nextSearchParams.set(DailyMenuSearchParam.SortOrder, optionName);
    setSearchParams(nextSearchParams);
  };

  const hasNoLunchProduct = useMemo(() => {
    return !hasPDL(user, "Lunch", selectedLocationId);
  }, [selectedLocationId]);

  // set sorting from query param
  useEffect(() => {
    if (menu && sortOrder && menu?.sort_options) {
      const index = menu.sort_options.findIndex(
        (option) => option.name === sortOrder
      );
      setSortOption(index < 0 ? 0 : index);
    }
  }, [menu]);

  const nextMenuDate = useMemo<DateString | undefined>(() => {
    const dayIndex = menuDates.findIndex((date) => date === selectedDate);
    const restDays = menuDates.slice(dayIndex + 1);
    return restDays.find((date) => !closedDates.includes(date));
  }, [menuDates, closedDates, selectedDate]);

  const nextMenuLink = useMemo<string | undefined>(() => {
    if (nextMenuDate) {
      const nextDateSearchParams = new URLSearchParams(searchParams);
      nextDateSearchParams.set(DailyMenuSearchParam.Date, nextMenuDate);
      return `/?${createSearchParams(nextDateSearchParams)}`;
    }
  }, [nextMenuDate, searchParams]);

  const goToNextMenuDate = (shouldReplace = false) => {
    if (nextMenuDate && nextMenuLink) {
      recordEvent(EventOrigin.PAGE, "view next menu", nextMenuDate);
      navigate(nextMenuLink, {replace: shouldReplace});
    }
  };

  return {
    selectedDate,
    selectedLocationId,
    isDayClosed,
    menuDates,
    hasNoLunchAccess,
    preSelectedItem,
    menu,
    hasNoLunchProduct,
    sortOption,
    setSortOption: handleUpdateSort,
    latestError,
    menuParams,
    closedDates,
    holidayDetails,
    nextMenuDate,
    nextMenuLink,
    goToNextMenuDate,
    isApiError: isMenuDatesError,
    isDateAdjusted,
    clearDayPartSearchParam,
    warning,
    clearWarning: () => setWarning(""),
  };
}
